import React, { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../../../Controllers/hooks";

import { Toast } from "primereact/toast";
import { Ripple } from "primereact/ripple";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { Nullable } from "primereact/ts-helpers";

import ErrorPage from "../../../../../Components/ErrorPage";
import DatePicker from "../../../../../Components/DatePicker";
import TextInput from "../../../../../Components/TextInput";
import { goToTop } from "../../../../../Components/goToTop";
import { getLoginUserRoleName, UserRoleName } from "../../../../../Utils/LoginUserRoleName";
import { formatDate, parseStringToDate } from "../../../../../Utils/getDate";
import { DutyHolderDetail, mapDutyHolderDetail } from "../../../../../Controllers/Types/DutyHolderDetails/DutyHolderDetailsTypes";
import { InputText } from "primereact/inputtext";
import { useSelector } from "react-redux";
import { RootState } from "../../../../../Controllers/Store";
import { hasPermission } from "../../../../../Controllers/Selectors/permissionsSelector";
import { getDutyHolderDetailsByFolderId } from "../../../../../Controllers/Thunks/BuildingInspectorThunks";

import { ProjectContactType } from "../../../../../Controllers/Types/Project/ProjectContactType";
import { updateDutyHolderClient, updateDutyHolderContractor, updateDutyHolderDesigner } from "../../../../../Api/BuildingInspectorService/BuildingInspectorService";
import { Checkbox } from "primereact/checkbox";
import PageLoader from "../../../../../Components/PageLoader";
import DetailLoader from "./DetailLoader";

const DutyHolderDetails: React.FC = () => {
    const location = useLocation();
    const navigate = useNavigate();
    const toast = useRef<Toast>(null);
    const dispatch = useAppDispatch();
    const [loading, setLoading] = useState<boolean>(false);
    const [dataState, setDataState] = useState<'Add' | 'Edit'>('Add');
    const [loginUserRoleName, setLoginUserRoleName] = useState<UserRoleName | ''>('');

    const projectData = location.state?.data;
    const folderId = Number(projectData.folder_id);
    const contactLists = projectData.projectAllContact as ProjectContactType[];

    const [clientData, setClientData] = useState<DutyHolderDetail | null>(null);
    const [principalDesignerData, setPrincipalDesignerData] = useState<DutyHolderDetail | null>(null);
    const [principalContractorData, setPrincipalContractorData] = useState<DutyHolderDetail | null>(null);

    const [isViewOnly, setIsViewOnly] = useState<boolean>(false);
    const [initialDataLoading, setInitialDataLoading] = useState<boolean>(true);

    const [clientLoading, setClientLoading] = useState<boolean>(false);
    const [pDesignerLoading, setPDesignerLoading] = useState<boolean>(false);
    const [pContractorLoading, setPContractorLoading] = useState<boolean>(false);


    const [detailsState, setDetailsState] = useState<'Client' | 'P. Designer' | 'P. Contractor' | null>(null);
    const [showDetailModal, setShowDetailModal] = useState<boolean>(false);

    const [clientName, setClientName] = useState<string>('');
    const [address, setAddress] = useState<string>('');
    const [email, setEmail] = useState<string>('');
    const [telephone, setTelephone] = useState<string>('');
    const [principalDesigner, setPrincipalDesigner] = useState<string>('');
    const [principalDesignerName, setPrincipalDesignerName] = useState<string>('');
    const [principalContractor, setPrincipalContractor] = useState<string>('');
    const [principalContractorName, setPrincipalContractorName] = useState<string>('');
    const [appointmentDate, setAppointmentDate] = useState<Nullable<Date>>(null);
    const [workCompleteDate, setWorkCompleteDate] = useState<Nullable<Date>>(null);

    const [clientNameError, setClientNameError] = useState<string>('');
    const [addressError, setAddressError] = useState<string>('');
    const [emailError, setEmailError] = useState<string>('');
    const [telephoneError, setTelephoneError] = useState<string>('');
    const [principalContractorError, setPrincipalContractorError] = useState<string>('');
    const [principalDesignerError, setPrincipalDesignerError] = useState<string>('');
    const [principalDesignerNameError, setPrincipalDesignerNameError] = useState<string>('');
    const [principalContractorNameError, setPrincipalContractorNameError] = useState<string>('');
    const [appointmentDateError, setAppointmentDateError] = useState<string>('');
    const [workCompleteDateError, setWorkCompleteDateError] = useState<string>('');

    const [detailUpdateHasChanges, setDetailUpdateHasChanges] = useState<boolean>(false);

    const [showContactListModal, setShowContactListModal] = useState<boolean>(false);
    const [contactData, setContactData] = useState<ProjectContactType[]>([]);
    const [selectedContact, setSelectedContact] = useState<ProjectContactType | null>(null);
    const [contactSearckKey, setContactSearckKey] = useState<string>('');

    const [showDeclareModal, setShowDeclareModal] = useState<boolean>(false);
    const [isAcceptDeclaration, setIsAcceptDeclaration] = useState<boolean>(false);
    const [declareThat, setDeclareThat] = useState<string>('');


    useEffect(() => {
        if (detailsState === 'Client') {
            setDeclareThat('The building work is complete, and, To the best of my knowledge, the building work complies with the Building Regulations.');
        } else if (detailsState === 'P. Designer') {
            setDeclareThat('I have fulfilled my duties under Part 2A of the Building Regulations 2010.');
        } else if (detailsState === 'P. Contractor') {
            setDeclareThat('I have fulfilled my duties under Part 2A of the Building Regulations 2010.');
        } else {
            setDeclareThat('');
        }
    }, [detailsState]);

    const handleCheckDeclareThat = () => {
        setShowDeclareModal(true);
    }

    const handleCloseDeclare = () => {
        setShowDeclareModal(false);
    }

    const handleAcceptDeclaration = () => {
        setShowDeclareModal(false);
        setIsAcceptDeclaration(prev => !prev);
    }

    useEffect(() => {
        const fetchUserRole = async () => {
            const roleName = await getLoginUserRoleName();
            setLoginUserRoleName(roleName);
        };

        fetchUserRole();
    }, []);


    // PERMISION HANDLER
    const useCanEdit = (screen: string, permission: string) => {
        return useSelector((state: RootState) =>
            hasPermission(state, screen, permission)
        );
    };

    const hasPermissionAddClient = useCanEdit("Dutyholder", "add_client");
    const hasPermissionEditClient = useCanEdit("Dutyholder", "edit_client");
    const hasPermissionAddDesigner = useCanEdit("Dutyholder", "add_designer");
    const hasPermissionEditDesigner = useCanEdit("Dutyholder", "edit_designer");
    const hasPermissionAddContractor = useCanEdit("Dutyholder", "add_contractor");
    const hasPermissionEditContractor = useCanEdit("Dutyholder", "edit_contractor");

    // for fetching data fro api tigger with state object actions
    useEffect(() => {
        dutyHolderDetailsGet();
    }, [folderId]);

    const dutyHolderDetailsGet = async () => {
        await dispatch(getDutyHolderDetailsByFolderId(folderId));
        setInitialDataLoading(false);
    }

    // this is get data from state list and set projectlist
    const dutyHolderDetailList = useAppSelector((state) => state?.dutyHolderDetails?.dutyHolderList);
    const loadingDutyHolderDetails = useAppSelector((state) => state?.dutyHolderDetails?.loader);
    const errorDutyHolderDetails = useAppSelector((state) => state?.dutyHolderDetails?.errorDisplay);

    useEffect(() => {
        if (dutyHolderDetailList && dutyHolderDetailList?.length !== 0) {
            setClientData(dutyHolderDetailList?.find(data => data?.type === 'Client') || null);
            setPrincipalDesignerData(dutyHolderDetailList?.find(data => data?.type === 'P. Designer') || null);
            setPrincipalContractorData(dutyHolderDetailList?.find(data => data?.type === 'P. Contractor') || null);
        } else {
            setClientData(null);
            setPrincipalDesignerData(null);
            setPrincipalContractorData(null);
        }
    }, [dutyHolderDetailList]);


    const checkValues = (data: DutyHolderDetail) => {
        var option: boolean = false;
        option = (data.name != null || data.address != null || data.email != null || data.telephone != null);

        return option;
    }

    useEffect(() => {
        if (clientData && clientData !== null && detailsState === 'Client') {
            if (clientName !== clientData?.client_name) {
                setDetailUpdateHasChanges(true);
            } else if (address !== clientData?.address) {
                setDetailUpdateHasChanges(true);
            } else if (email !== clientData?.email) {
                setDetailUpdateHasChanges(true);
            } else if (telephone !== clientData?.telephone) {
                setDetailUpdateHasChanges(true);
            } else if (formatDate(workCompleteDate!) !== clientData?.work_complete_date) {
                setDetailUpdateHasChanges(true);
            } else {
                setDetailUpdateHasChanges(false);
            }
        }

        if (principalDesignerData && principalDesignerData !== null && detailsState === 'P. Designer') {
            if (principalDesigner !== principalDesignerData?.principal_designer) {
                setDetailUpdateHasChanges(true);
            } else if (principalDesignerName !== principalDesignerData?.name) {
                setDetailUpdateHasChanges(true);
            } else if (address !== principalDesignerData?.address) {
                setDetailUpdateHasChanges(true);
            } else if (email !== principalDesignerData?.email) {
                setDetailUpdateHasChanges(true);
            } else if (telephone !== principalDesignerData?.telephone) {
                setDetailUpdateHasChanges(true);
            } else if (formatDate(appointmentDate!) !== principalDesignerData?.appointment_date) {
                setDetailUpdateHasChanges(true);
            } else {
                setDetailUpdateHasChanges(false);
            }
        }

        if (principalContractorData && principalContractorData !== null && detailsState === 'P. Contractor') {
            if (principalContractor !== principalContractorData?.principal_designer) {
                setDetailUpdateHasChanges(true);
            } else if (principalContractorName !== principalContractorData?.name) {
                setDetailUpdateHasChanges(true);
            } else if (address !== principalContractorData?.address) {
                setDetailUpdateHasChanges(true);
            } else if (email !== principalContractorData?.email) {
                setDetailUpdateHasChanges(true);
            } else if (telephone !== principalContractorData?.telephone) {
                setDetailUpdateHasChanges(true);
            } else if (formatDate(appointmentDate!) !== principalContractorData?.appointment_date) {
                setDetailUpdateHasChanges(true);
            } else {
                setDetailUpdateHasChanges(false);
            }
        }
    }, [
        detailsState,
        clientData,
        principalDesignerData,
        principalContractorData,
        address,
        email,
        telephone,
        clientName,
        workCompleteDate,
        principalDesignerName,
        principalDesigner,
        principalContractor,
        principalContractorName,
        appointmentDate,
    ]);

    const handleCloseDetailModal = () => {
        setShowDetailModal(false);

        handleResetFields();
        handleResetErrors();
    }

    const handleResetFields = () => {
        setAddress('');
        setEmail('');
        setTelephone('');

        setClientName('');
        setWorkCompleteDate(null);

        setPrincipalDesigner('');
        setPrincipalDesignerName('');

        setPrincipalContractor('');
        setPrincipalContractorName('');

        setAppointmentDate(null);

        setIsAcceptDeclaration(false);
    }

    const handleResetErrors = () => {
        setAddressError('');
        setEmailError('');
        setTelephoneError('');

        setClientNameError('');
        setWorkCompleteDateError('');

        setPrincipalDesignerNameError('');
        setPrincipalDesignerError('');

        setPrincipalContractorNameError('');
        setPrincipalContractorError('');

        setAppointmentDateError('');
    }

    const handleAddDetail = (state: 'Client' | 'P. Designer' | 'P. Contractor') => {
        setShowContactListModal(true);
        setDetailsState(state);
    }

    const handleEditDetails = (state: 'Client' | 'P. Designer' | 'P. Contractor') => {
        setDataState('Edit')
        setDetailsState(state);
        setShowDetailModal(true);

        if (clientData && clientData !== null && state === 'Client') {
            setClientName(clientData?.client_name!);
            setAddress(clientData?.address!);
            setEmail(clientData?.email!);
            setTelephone(clientData?.telephone!);
            setWorkCompleteDate(parseStringToDate(clientData?.work_complete_date!));
            setIsAcceptDeclaration((clientData?.declare_that && clientData?.declare_that !== '') ? true : false)
        }

        if (principalDesignerData && principalDesignerData !== null && state === 'P. Designer') {
            setPrincipalDesigner(principalDesignerData?.principal_designer!);
            setPrincipalDesignerName(principalDesignerData?.name!);
            setAddress(principalDesignerData?.address!);
            setEmail(principalDesignerData?.email!);
            setTelephone(principalDesignerData?.telephone!);
            setAppointmentDate(parseStringToDate(principalDesignerData?.appointment_date!));
            setIsAcceptDeclaration((principalDesignerData?.declare_that && principalDesignerData?.declare_that !== '') ? true : false)
        }

        if (principalContractorData && principalContractorData !== null && state === 'P. Contractor') {
            setPrincipalContractor(principalContractorData?.principal_designer!);
            setPrincipalContractorName(principalContractorData?.name!);
            setAddress(principalContractorData?.address!);
            setEmail(principalContractorData?.email!);
            setTelephone(principalContractorData?.telephone!);
            setAppointmentDate(parseStringToDate(principalContractorData?.appointment_date!));
            setIsAcceptDeclaration((principalContractorData?.declare_that && principalContractorData?.declare_that !== '') ? true : false)
        }
    }

    const handleUpdateDetails = async (e: React.FormEvent, state: 'Client' | 'P. Designer' | 'P. Contractor') => {
        e.preventDefault();

        setLoading(true);

        var data: any = {};
        if (detailsState === "Client") {
            data = await updateDutyHolderClient(
                folderId,
                clientName,
                address,
                email,
                telephone,
                formatDate(workCompleteDate!),
                declareThat
            );
        } else if (detailsState === "P. Designer") {
            data = await updateDutyHolderDesigner(
                folderId,
                principalDesigner,
                principalDesignerName,
                address,
                email,
                telephone,
                formatDate(appointmentDate!),
                declareThat
            );
        } else {
            data = await updateDutyHolderContractor(
                folderId,
                principalContractor,
                principalContractorName,
                address,
                email,
                telephone,
                formatDate(appointmentDate!),
                declareThat
            );
        }

        setLoading(false);

        if (data.statusCode === 200) {
            if (state === 'Client') {
                setClientLoading(true);
            } else if (state === 'P. Designer') {
                setPDesignerLoading(true);
            } else {
                setPContractorLoading(true);
            }

            handleCloseDetailModal();
            await dutyHolderDetailsGet();

            if (state === 'Client') {
                setClientLoading(false);
            } else if (state === 'P. Designer') {
                setPDesignerLoading(false);
            } else {
                setPContractorLoading(false);
            }

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: `${state === 'Client' ? 'Client' : state === 'P. Designer' ? 'Principal designer' : 'Principal contractor'} details updated successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: `There was an error updating the ${state === 'Client' ? 'client' : state === 'P. Designer' ? 'principal designer' : 'principal contractor'} details.`,
                    life: 3000,
                });
            }
        }
    }

    /* Modal header */
    const detailModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Edit {detailsState === 'Client' ? 'Client' : detailsState === 'P. Designer' ? 'Principal Designer' : detailsState === 'P. Contractor' ? 'Principal Contractor' : ''}
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseDetailModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    /* Modal footer */
    const detailModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseDetailModal}
            />

            <Button
                label={loading ? 'Processing' : dataState === 'Add' ? 'Submit' : 'Update'}
                onClick={(e) => handleUpdateDetails(e, detailsState!)}
                loading={loading} className="custom_btn primary"
                disabled={(dataState === 'Edit' && !detailUpdateHasChanges) || !isAcceptDeclaration}
            />
        </div>
    );


    /* ********************************* Contact ********************************* */
    useEffect(() => {
        if (contactLists && contactLists?.length > 0) {
            const filteredData = contactLists.filter(
                (contact) =>
                    contact?.first_name!.toLowerCase()
                        .includes(contactSearckKey.toLowerCase()) ||
                    contact?.last_name!.toLowerCase()
                        .includes(contactSearckKey.toLowerCase())
            );
            setContactData(filteredData);
        } else {
            setContactData([]);
        }
    }, [contactSearckKey]);

    const handleShowContactModal = () => {
        setShowContactListModal(true);
    }

    const handleCloseContactModal = () => {
        setShowContactListModal(false);
        setSelectedContact(null);
    }

    const handleSelectContact = (contact: ProjectContactType) => {
        setSelectedContact(prev => {
            if (prev && prev.id === contact.id) {
                return null;
            }
            return contact;
        });
    };


    const handleSaveDetail = async (e: React.FormEvent, state: 'Client' | 'P. Designer' | 'P. Contractor') => {
        e.preventDefault();

        setLoading(true);

        var data: any = {};
        if (detailsState === "Client") {
            data = await updateDutyHolderClient(
                folderId,
                selectedContact?.first_name + " " + selectedContact?.last_name,
                selectedContact?.address!,
                selectedContact?.email!,
                selectedContact?.mobile!,
                "", ""
            );
        } else if (detailsState === "P. Designer") {
            data = await updateDutyHolderDesigner(
                folderId,
                selectedContact?.company_name!,
                selectedContact?.first_name + " " + selectedContact?.last_name,
                selectedContact?.address!,
                selectedContact?.email!,
                selectedContact?.mobile!,
                "", ""
            );
        } else {
            data = await updateDutyHolderContractor(
                folderId,
                selectedContact?.company_name!,
                selectedContact?.first_name + " " + selectedContact?.last_name,
                selectedContact?.address!,
                selectedContact?.email!,
                selectedContact?.mobile!,
                "", ""
            );
        }
        setLoading(false);

        if (data.statusCode === 200) {
            if (state === 'Client') {
                setClientLoading(true);
            } else if (state === 'P. Designer') {
                setPDesignerLoading(true);
            } else {
                setPContractorLoading(true);
            }

            handleCloseContactModal();
            await dutyHolderDetailsGet();

            if (state === 'Client') {
                setClientLoading(false);
            } else if (state === 'P. Designer') {
                setPDesignerLoading(false);
            } else {
                setPContractorLoading(false);
            }

            if (toast.current) {
                toast.current.show({
                    severity: "success",
                    summary: "Success",
                    detail: `${state === 'Client' ? 'Client' : state === 'P. Designer' ? 'Principal designer' : 'Principal contractor'} details added successfully.`,
                    life: 3000,
                });
            }
        } else {
            if (toast.current) {
                toast.current.show({
                    severity: "error",
                    summary: "Failed",
                    detail: `There was an error adding the ${state === 'Client' ? 'Client' : state === 'P. Designer' ? 'Principal designer' : 'Principal contractor'} details.`,
                    life: 3000,
                });
            }
        }
    }


    /* Modal header */
    const contactModalHeader = (
        <div className="custom_modal_header_inner">
            <h5 className="modal-title fs-5">
                Add {detailsState === 'Client' ? 'Client' : detailsState === 'P. Designer' ? 'Principal designer' : 'Principal contractor'}
            </h5>

            <button type="button" className="close_modal_btn p-ripple"
                onClick={handleCloseContactModal}>
                <i className="bi bi-x-circle"></i>
                <Ripple />
            </button>
        </div>
    )

    /* Modal footer */
    const contactModalFooter = (
        <div className="custom_modal_footer">
            <Button
                label="Close"
                className="custom_btn secondary"
                onClick={handleCloseContactModal}
            />

            <Button
                label={loading ? 'Processing' : 'Add'}
                onClick={(e) => handleSaveDetail(e, detailsState!)}
                loading={loading}
                className="custom_btn primary"
                disabled={selectedContact === null}
            />
        </div>
    );
    /* *************************************************************************** */


    /* **************************** Common functions ***************************** */
    const goBack = () => {
        navigate(-1);
        goToTop();
    };
    /* *************************************************************************** */

    return (
        <React.Fragment>
            <Toast ref={toast} />

            <div className="main_content layout_padding show_side_bar">
                {loadingDutyHolderDetails && initialDataLoading ? (
                    <PageLoader />
                ) : errorDutyHolderDetails ? (
                    <ErrorPage errorMessage={errorDutyHolderDetails} />
                ) : (
                    <div className="page_content mt-0">
                        <div className="page_breadcrumb_area">
                            <ul className="page_breadcrumb">
                                <li className="page_breadcrumb_item has_child has_link" onClick={() => navigate(-1)}>
                                    {projectData.job_number} : {projectData.address}
                                </li>
                                <li className="page_breadcrumb_item has_child">
                                    {projectData.folder}
                                </li>
                                <li className="page_breadcrumb_item active">
                                    {projectData.feature_name}
                                </li>
                            </ul>

                            <button className="back_btn p-ripple" onClick={goBack}>
                                <i className="bi bi-arrow-left me-2"></i>
                                Back
                                <Ripple />
                            </button>
                        </div>

                        {/* {!isViewOnly && (
                            <div className="page_header_area justify-content-end mt-3">

                                <div className="page_action_area">
                                    <button
                                        className={`add_data_link_btn p-ripple`}
                                        onClick={() => handleAddDetail('Client')}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Client</span>
                                        <Ripple />
                                    </button>

                                    <button
                                        className={`add_data_link_btn p-ripple`}
                                        onClick={() => handleAddDetail('P. Designer')}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Designer</span>
                                        <Ripple />
                                    </button>

                                    <button
                                        className={`add_data_link_btn p-ripple`}
                                        onClick={() => handleAddDetail('P. Contractor')}
                                    >
                                        <i className="bi bi-plus-circle"></i>
                                        <span>Contractor</span>
                                        <Ripple />
                                    </button>
                                </div>
                            </div>
                        )} */}

                        <div className="detail_container_section mt-4">
                            <div className="row detail_container_row">
                                {/* Client */}
                                <div className="col-12 col-xl-4 col-md-6 detail_container_column">
                                    {clientLoading ? (
                                        <DetailLoader contentCount={5} />
                                    ) : (
                                        <article className="detail_container_area">
                                            <div className="detail_container_header">
                                                <h6>Client</h6>

                                                {!isViewOnly && (
                                                    <React.Fragment>
                                                        {clientData && clientData !== null && checkValues(clientData) ? (
                                                            <React.Fragment>
                                                                {hasPermissionAddClient && (
                                                                    <button className="detail_edit_btn is_btn p-ripple"
                                                                        onClick={() => handleEditDetails('Client')}
                                                                        disabled={false} // disable this button role wise
                                                                    >
                                                                        <i className="bi bi-pencil-square"></i>
                                                                        <Ripple />
                                                                    </button>
                                                                )}
                                                            </React.Fragment>
                                                        ) : (
                                                            <React.Fragment>
                                                                {hasPermissionEditClient && (
                                                                    <button className="detail_edit_btn is_btn p-ripple"
                                                                        onClick={() => handleAddDetail('Client')}
                                                                        disabled={false} // disable this button role wise
                                                                    >
                                                                        <i className="bi bi-plus-circle"></i>
                                                                        <Ripple />
                                                                    </button>
                                                                )}
                                                            </React.Fragment>
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </div>

                                            <div className="detail_container_body">
                                                {clientData && clientData !== null ? (
                                                    <div className="detail_container">
                                                        <div className="detail_content_group">
                                                            <h6>Client :</h6>
                                                            <p>
                                                                {clientData?.client_name ? (
                                                                    <span>{clientData?.client_name}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Address :</h6>
                                                            <p>
                                                                {clientData?.address ? (
                                                                    <span>{clientData?.address}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Email :</h6>
                                                            <p>
                                                                {clientData?.email ? (
                                                                    <span>{clientData?.email}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Telephone :</h6>
                                                            <p>
                                                                {clientData?.telephone ? (
                                                                    <span>{clientData?.telephone}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group no_divider">
                                                            <h6>Work Complete Date :</h6>
                                                            <p>
                                                                {clientData?.work_complete_date ? (
                                                                    <span>{clientData?.work_complete_date}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group with_bg">
                                                            <h6>I declare that :</h6>
                                                            <p>
                                                                {clientData?.declare_that ? (
                                                                    <span>{clientData?.declare_that}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="empty_detail_container custom">
                                                        <img src="/assets/images/no_data/no_data.svg" alt="No data" />
                                                        <h6>
                                                            <i className="bi bi-exclamation-circle me-1"></i>
                                                            No data found for client!
                                                        </h6>
                                                    </div>
                                                )}
                                            </div>
                                        </article>
                                    )}
                                </div>

                                {/* Principal Designer */}
                                <div className="col-12 col-xl-4 col-md-6 detail_container_column">
                                    {pDesignerLoading ? (
                                        <DetailLoader />
                                    ) : (
                                        <article className="detail_container_area">
                                            <div className="detail_container_header">
                                                <h6>Principal Designer</h6>

                                                {!isViewOnly && (
                                                    <React.Fragment>
                                                        {principalDesignerData && principalDesignerData !== null && checkValues(principalDesignerData) ? (
                                                            <React.Fragment>
                                                                {hasPermissionAddDesigner && (
                                                                    <button className="detail_edit_btn is_btn p-ripple"
                                                                        onClick={() => handleEditDetails('P. Designer')}
                                                                        disabled={false} // disable this button role wise
                                                                    >
                                                                        <i className="bi bi-pencil-square"></i>
                                                                        <Ripple />
                                                                    </button>
                                                                )}
                                                            </React.Fragment>
                                                        ) : (
                                                            <React.Fragment>
                                                                {hasPermissionEditDesigner && (
                                                                    <button className="detail_edit_btn is_btn p-ripple"
                                                                        onClick={() => handleAddDetail('P. Designer')}
                                                                        disabled={false} // disable this button role wise
                                                                    >
                                                                        <i className="bi bi-plus-circle"></i>
                                                                        <Ripple />
                                                                    </button>
                                                                )}
                                                            </React.Fragment>
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </div>

                                            <div className="detail_container_body">
                                                {principalDesignerData && principalDesignerData !== null ? (
                                                    <div className="detail_container">
                                                        <div className="detail_content_group">
                                                            <h6>Principal Designer :</h6>
                                                            <p>
                                                                {principalDesignerData?.principal_designer ? (
                                                                    <span>{principalDesignerData?.principal_designer}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Name :</h6>
                                                            <p>
                                                                {principalDesignerData?.name ? (
                                                                    <span>{principalDesignerData?.name}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Address :</h6>
                                                            <p>
                                                                {principalDesignerData?.address ? (
                                                                    <span>{principalDesignerData?.address}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Email :</h6>
                                                            <p>
                                                                {principalDesignerData?.email ? (
                                                                    <span>{principalDesignerData?.email}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Telephone :</h6>
                                                            <p>
                                                                {principalDesignerData?.telephone ? (
                                                                    <span>{principalDesignerData?.telephone}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group no_divider">
                                                            <h6>Appointment Date :</h6>
                                                            <p>
                                                                {principalDesignerData?.appointment_date ? (
                                                                    <span>{principalDesignerData?.appointment_date}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group with_bg">
                                                            <h6>I declare that :</h6>
                                                            <p>
                                                                {principalDesignerData?.declare_that ? (
                                                                    <span>{principalDesignerData?.declare_that}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="empty_detail_container custom">
                                                        <img src="/assets/images/no_data/no_data.svg" alt="No data" />
                                                        <h6>
                                                            <i className="bi bi-exclamation-circle me-1"></i>
                                                            No data found for principal designer!
                                                        </h6>
                                                    </div>
                                                )}
                                            </div>
                                        </article>
                                    )}
                                </div>

                                {/* Principal Contractor */}
                                <div className="col-12 col-xl-4 col-md-6 detail_container_column">
                                    {pContractorLoading ? (
                                        <DetailLoader />
                                    ) : (
                                        <article className="detail_container_area">
                                            <div className="detail_container_header">
                                                <h6>Principal Contractor</h6>

                                                {!isViewOnly && (
                                                    <React.Fragment>
                                                        {principalContractorData && principalContractorData !== null && checkValues(principalContractorData) ? (
                                                            <React.Fragment>
                                                                {hasPermissionAddContractor && (
                                                                    <button className="detail_edit_btn is_btn p-ripple"
                                                                        onClick={() => handleEditDetails('P. Contractor')}
                                                                        disabled={false} // disable this button role wise
                                                                    >
                                                                        <i className="bi bi-pencil-square"></i>
                                                                        <Ripple />
                                                                    </button>
                                                                )}
                                                            </React.Fragment>
                                                        ) : (
                                                            <React.Fragment>
                                                                {hasPermissionEditContractor && (
                                                                    <button className="detail_edit_btn is_btn p-ripple"
                                                                        onClick={() => handleAddDetail('P. Contractor')}
                                                                        disabled={false} // disable this button role wise
                                                                    >
                                                                        <i className="bi bi-plus-circle"></i>
                                                                        <Ripple />
                                                                    </button>
                                                                )}
                                                            </React.Fragment>
                                                        )}
                                                    </React.Fragment>
                                                )}
                                            </div>

                                            <div className="detail_container_body">
                                                {principalContractorData && principalContractorData !== null ? (
                                                    <div className="detail_container">
                                                        <div className="detail_content_group">
                                                            <h6>Principal Contractor :</h6>
                                                            <p>
                                                                {principalContractorData?.principal_designer ? (
                                                                    <span>{principalContractorData?.principal_designer}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Name :</h6>
                                                            <p>
                                                                {principalContractorData?.name ? (
                                                                    <span>{principalContractorData?.name}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Address :</h6>
                                                            <p>
                                                                {principalContractorData?.address ? (
                                                                    <span>{principalContractorData?.address}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Email :</h6>
                                                            <p>
                                                                {principalContractorData?.email ? (
                                                                    <span>{principalContractorData?.email}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group">
                                                            <h6>Telephone :</h6>
                                                            <p>
                                                                {principalContractorData?.telephone ? (
                                                                    <span>{principalContractorData?.telephone}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group no_divider">
                                                            <h6>Appointment Date :</h6>
                                                            <p>
                                                                {principalContractorData?.appointment_date ? (
                                                                    <span>{principalContractorData?.appointment_date}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>

                                                        <div className="detail_content_group with_bg">
                                                            <h6>I declare that :</h6>
                                                            <p>
                                                                {principalContractorData?.declare_that ? (
                                                                    <span>{principalContractorData?.declare_that}</span>
                                                                ) : (
                                                                    <span className="text_italic">N/A</span>
                                                                )}
                                                            </p>
                                                        </div>
                                                    </div>
                                                ) : (
                                                    <div className="empty_detail_container custom">
                                                        <img src="/assets/images/no_data/no_data.svg" alt="No data" />
                                                        <h6>
                                                            <i className="bi bi-exclamation-circle me-1"></i>
                                                            No data found for principal contractor!
                                                        </h6>
                                                    </div>
                                                )}
                                            </div>
                                        </article>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>


            {/* Detail modal */}
            <Dialog
                visible={showDetailModal}
                headerClassName="custom_modal_header"
                header={detailModalHeader}
                footer={detailModalFooter}
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseDetailModal}
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-2 p-sm-4 pt-sm-2 pb-sm-2 w-100">
                        <div className="row">
                            {detailsState === 'Client' && (
                                <div className="col-12">
                                    <TextInput
                                        key={`clientName`}
                                        id="clientName"
                                        label="Client name"
                                        labelHtmlFor="clientName"
                                        required={true}
                                        value={clientName}
                                        inputType="text"
                                        placeholder="Client name"
                                        onChange={(e: React.ChangeEvent<HTMLInputElement>) => setClientName(e.target.value)}
                                        error={clientNameError}
                                    />
                                </div>
                            )}

                            {detailsState === 'P. Designer' && (
                                <React.Fragment>
                                    <div className="col-12">
                                        <TextInput
                                            key={`principalDesigner`}
                                            id="principalDesigner"
                                            label="Principal designer"
                                            labelHtmlFor="principalDesigner"
                                            required={true}
                                            value={principalDesigner}
                                            placeholder="Principal designer"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrincipalDesigner(e.target.value)}
                                            error={principalDesignerError}
                                        />
                                    </div>

                                    <div className="col-12">
                                        <TextInput
                                            key={`principalDesignerName`}
                                            id="principalDesignerName"
                                            label="Name"
                                            labelHtmlFor="principalDesignerName"
                                            required={true}
                                            value={principalDesignerName}
                                            placeholder="Name"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrincipalDesignerName(e.target.value)}
                                            error={principalDesignerNameError}
                                        />
                                    </div>
                                </React.Fragment>
                            )}


                            {detailsState === 'P. Contractor' && (
                                <React.Fragment>
                                    <div className="col-12">
                                        <TextInput
                                            key={`principalContractor`}
                                            id="principalContractor"
                                            label="Principal contractor"
                                            labelHtmlFor="principalContractor"
                                            required={true}
                                            value={principalContractor}
                                            placeholder="Principal contractor"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrincipalContractor(e.target.value)}
                                            error={principalContractorError}
                                        />
                                    </div>

                                    <div className="col-12">
                                        <TextInput
                                            key={`principalContractorName`}
                                            id="principalContractorName"
                                            label="Name"
                                            labelHtmlFor="principalContractorName"
                                            required={true}
                                            value={principalContractorName}
                                            placeholder="Name"
                                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPrincipalContractorName(e.target.value)}
                                            error={principalContractorNameError}
                                        />
                                    </div>
                                </React.Fragment>
                            )}

                            <div className="col-12">
                                <TextInput
                                    key={`address`}
                                    id="address"
                                    label="Address"
                                    labelHtmlFor="address"
                                    required={true}
                                    inputType="text"
                                    value={address}
                                    placeholder="Address"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAddress(e.target.value)}
                                    error={addressError}
                                />
                            </div>

                            <div className="col-12">
                                <TextInput
                                    key={`email`}
                                    id="email"
                                    label="Email"
                                    labelHtmlFor="email"
                                    required={true}
                                    inputType="email"
                                    value={email}
                                    placeholder="Email"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                                    error={emailError}
                                />
                            </div>

                            <div className="col-12">
                                <TextInput
                                    key={`telephone`}
                                    id="telephone"
                                    label="Telephone"
                                    labelHtmlFor="telephone"
                                    required={true}
                                    inputType="text"
                                    value={telephone}
                                    placeholder="Telephone"
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTelephone(e.target.value)}
                                    error={telephoneError}
                                />
                            </div>

                            {detailsState === 'Client' && (
                                <div className="col-12">
                                    <DatePicker
                                        key={`workCompleteDate`}
                                        id="workCompleteDate"
                                        label="Work complete date"
                                        labelHtmlFor="workCompleteDate"
                                        required={true}
                                        value={workCompleteDate}
                                        placeholder="dd/mm/yyyy"
                                        onChange={setWorkCompleteDate}
                                        error={workCompleteDateError}
                                        showClear
                                        onClear={() => setWorkCompleteDate(null)}
                                    />
                                </div>
                            )}

                            {(detailsState === 'P. Designer' || detailsState === 'P. Contractor') && (
                                <div className="col-12">
                                    <DatePicker
                                        key={`appointmentDate`}
                                        id="appointmentDate"
                                        label="Appointment date"
                                        labelHtmlFor="appointmentDate"
                                        required={true}
                                        value={appointmentDate}
                                        placeholder="dd/mm/yyyy"
                                        onChange={setAppointmentDate}
                                        error={appointmentDateError}
                                        showClear
                                        onClear={() => setAppointmentDate(null)}
                                    />
                                </div>
                            )}

                            <div className="col-12">
                                <div className="page_form_group mb-0">
                                    <div className="response_state">
                                        <Checkbox
                                            inputId="declare"
                                            onChange={handleCheckDeclareThat}
                                            checked={isAcceptDeclaration}
                                        />
                                        <label htmlFor="declare">To declare...</label>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Contact modal */}
            <Dialog
                visible={showContactListModal}
                headerClassName="custom_modal_header"
                header={contactModalHeader}
                footer={selectedContact !== null ? contactModalFooter : null}
                dismissableMask
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseContactModal}
                contentClassName="pt-0"
            >
                <div className="custom_modal_body">
                    <div className="p-1 pt-0 p-sm-4 pt-sm-0 pb-sm-2 w-100">
                        <div className="data_select_modal_header px-0 py-3 pt-4 mb-3">
                            <div className="data_search_input_area">
                                <InputText
                                    key={"contactSearckKey"}
                                    id="contactSearckKey"
                                    value={contactSearckKey}
                                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContactSearckKey(e.target.value)}
                                    className="custom_form_input data_search_input"
                                    placeholder="Search contact by name"
                                    autoComplete="off"
                                    autoFocus
                                    type={"search"}
                                />
                                {contactSearckKey === "" ? (
                                    <i className="bi bi-search"></i>
                                ) : (
                                    <i
                                        className="bi bi-x-lg data_search_input_clear"
                                        onClick={() => setContactSearckKey("")}
                                    ></i>
                                )}
                            </div>
                        </div>

                        <div className="row px-sm-3 px-1">
                            <div className="col-12">
                                {(contactData && contactData?.length > 0) ? (
                                    <div className="data_select_option_area">
                                        {contactData?.map((contact) => {
                                            const profileImage = contact?.profile_image || "";
                                            const firstName = contact?.first_name || "";
                                            const lastName = contact?.last_name || "";
                                            const roleName = contact?.role_name || "";
                                            const contactNumber = contact?.mobile || "";
                                            const fullName = firstName + " " + lastName;
                                            const profileLetter = firstName[0] + lastName[0];

                                            return (
                                                <React.Fragment>
                                                    <div
                                                        key={contact?.id}
                                                        className={`contact_select_option ${selectedContact?.id === contact?.id && "selected"} p-ripple`}
                                                        onClick={() => handleSelectContact(contact)}
                                                    >
                                                        <div className="contact_prof_avatar">
                                                            {profileImage ? (
                                                                <img src={profileImage} alt={fullName} />
                                                            ) : (
                                                                <span>{profileLetter}</span>
                                                            )}
                                                        </div>
                                                        <div className="contact_prof_info">
                                                            <h6>{fullName}</h6>
                                                            <p>
                                                                <span>{roleName}</span>
                                                                <span className="text_light">
                                                                    {(roleName && contactNumber) && ' | '}
                                                                </span>
                                                                <span>{contactNumber}</span>
                                                            </p>
                                                            <p> {contact.by} </p>
                                                        </div>
                                                        <Ripple
                                                            pt={{ root: { style: { background: "rgba(164, 96, 253, 0.2)" }, }, }}
                                                        />
                                                    </div>
                                                </React.Fragment>
                                            );
                                        })}
                                    </div>
                                ) : (
                                    <div className="no_records_found">
                                        <i className="bi bi-exclamation-circle me-1"></i>
                                        No contact(s) found.
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </Dialog>
            {/*  */}


            {/* Declaration modal */}
            <Dialog
                headerClassName="d-none"
                visible={showDeclareModal}
                className="custom_modal_dialog modal_dialog_sm"
                onHide={handleCloseDeclare}
                dismissableMask
            >
                <div className="custom_modal_body">
                    <button className="custom_modal_close position-absolute float p-ripple"
                        onClick={handleCloseDeclare}>
                        <i className="bi bi-x-lg"></i>
                        <Ripple />
                    </button>

                    <div className="use_case_feature_info">
                        <h6 className="review_head">
                            To {isAcceptDeclaration ? 'decline' : 'confirm'} declaration
                        </h6>

                        <p className="review_paragraph custom text-start">
                            {isAcceptDeclaration ? 'Are you sure you want to decline the declaration?' : declareThat}
                        </p>

                        <div className="review_action_area">
                            <p>
                                {!isAcceptDeclaration ? 'I hearby accept this declaration.' : ''}
                            </p>

                            <Button
                                label={isAcceptDeclaration ? 'Decline' : 'Accept'}
                                onClick={handleAcceptDeclaration}
                                className="custom_btn primary"
                            />
                        </div>
                    </div>
                </div>
            </Dialog>
        </React.Fragment >
    )
}

export default DutyHolderDetails;